import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Loader2, RefreshCw, Smartphone, XCircle, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { operatorOf, useMomoPhone } from "@/lib/operators";

export const OperatorHint = ({ detected, testId }) => {
  const { t } = useI18n();
  if (!detected) return <p className="text-xs text-slate-500" data-testid={testId}>{t("deposit.opAuto")}</p>;
  if (detected === "unknown") return <p className="text-xs font-semibold text-red-400" data-testid={testId}>{t("errors.unsupported_operator")}</p>;
  return <p className="text-xs text-emerald-300" data-testid={testId}>{t("deposit.opDetected", { op: detected === "MTN" ? "MTN MoMo" : "Orange Money" })}</p>;
};

export const usePublicSettings = () =>
  useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings/public")).data });

const OPERATORS = [
  { id: "MTN", label: "MTN MoMo", cls: "bg-[#FFCC00] text-black" },
  { id: "ORANGE", label: "Orange Money", cls: "bg-[#FF6600] text-white" },
];

const DepositForm = ({ settings, onCreated }) => {
  const { t, tErr } = useI18n();
  const { user, setUser } = useAuth();
  const [saved, remember] = useMomoPhone(user, setUser);
  const [phone, setPhone] = useState(saved);
  const detected = operatorOf(phone);
  const operator = detected === "ORANGE" ? "ORANGE" : "MTN";
  const [amount, setAmount] = useState(String(settings.deposit_max));
  const [busy, setBusy] = useState(false);
  const quick = settings.campay_mode === "DEV" ? [5, 10, 25] : [500, 1000, 2000, 5000];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/deposit", { amount: parseInt(amount, 10), phone, operator });
      remember(data.phone);
      onCreated(data);
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="deposit-form">
      <div className="space-y-2">
        <Label className="text-slate-300">{t("deposit.operator")}</Label>
        <div className="grid grid-cols-2 gap-3">
          {OPERATORS.map((o) => (
            <div key={o.id} data-testid={`deposit-operator-${o.id.toLowerCase()}`} data-active={detected === o.id}
              className={`flex h-14 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-colors ${
                detected === o.id ? "border-amber-400 bg-white/10" : "border-white/10 bg-white/5 text-slate-500"}`}>
              <span className={`h-3 w-3 rounded-full ${o.cls}`} />
              {o.label}
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-phone" className="text-slate-300">{t("deposit.phone")}</Label>
        <Input id="dep-phone" type="tel" inputMode="tel" required placeholder="6XXXXXXXX" value={phone}
          onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/5 font-mono text-base" data-testid="deposit-phone-input" />
        <OperatorHint detected={detected} testId="deposit-operator-hint" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-amount" className="text-slate-300">{t("deposit.amount")}</Label>
        <Input id="dep-amount" type="number" inputMode="numeric" required min={settings.deposit_min} max={settings.deposit_max} step={1}
          value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/5 font-mono text-base" data-testid="deposit-amount-input" />
        <div className="flex gap-2">
          {quick.map((q) => (
            <button key={q} type="button" onClick={() => setAmount(String(q))} data-testid={`deposit-quick-${q}`}
              className="h-10 flex-1 rounded-lg border border-white/10 bg-white/5 font-mono text-xs font-semibold text-slate-300 transition-transform active:scale-95">
              {q}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">{t("deposit.range", { min: settings.deposit_min, max: settings.deposit_max })}</p>
      </div>
      <button type="submit" disabled={busy || !detected || detected === "unknown"} data-testid="campay-deposit-button"
        className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display font-bold text-[#0B0D12] transition-transform active:scale-[0.98] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Smartphone className="h-5 w-5" />}
        {t("deposit.submit")}
      </button>
    </form>
  );
};

const RESULT = {
  SUCCESSFUL: { icon: CheckCircle2, cls: "text-emerald-400", k: "success" },
  FAILED: { icon: XCircle, cls: "text-red-400", k: "failed" },
  EXPIRED: { icon: XCircle, cls: "text-red-400", k: "failed" },
  REVIEW: { icon: AlertTriangle, cls: "text-amber-400", k: "review" },
};

const DepositStatus = ({ payment, setPayment, onReset, onClose }) => {
  const { t, tErr, lang } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const pending = payment.status === "PENDING";

  const verify = async (silent) => {
    if (!silent) setBusy(true);
    try {
      const { data } = await api.post(`/payments/${payment.id}/verify`);
      setPayment(data);
      if (data.status !== "PENDING") {
        qc.invalidateQueries({ queryKey: ["wallet"] });
        qc.invalidateQueries({ queryKey: ["ledger"] });
        qc.invalidateQueries({ queryKey: ["payments"] });
      }
    } catch (err) {
      if (!silent) toast.error(tErr(errCode(err)));
    } finally {
      if (!silent) setBusy(false);
    }
  };

  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(() => verify(true), 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, payment.id]);

  if (pending) {
    return (
      <div className="space-y-5 text-center" data-testid="deposit-pending">
        <div className="pulse-ring mx-auto grid h-20 w-20 place-items-center rounded-full bg-amber-500/15">
          <Smartphone className="h-9 w-9 text-amber-400" />
        </div>
        <h3 className="font-display text-xl font-bold">{t("deposit.pendingTitle")}</h3>
        <p className="text-sm text-slate-400">{t("deposit.pendingText", { phone: payment.phone })}</p>
        <p className="font-mono text-2xl font-semibold text-amber-300" data-testid="deposit-pending-amount">{formatXAF(payment.amount, lang)}</p>
        {payment.ussd_code && (
          <p className="text-sm text-slate-400">
            {t("deposit.ussd")} <span className="font-mono text-white" data-testid="deposit-ussd-code">{payment.ussd_code}</span>
          </p>
        )}
        <button onClick={() => verify(false)} disabled={busy} data-testid="verify-payment-btn"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full border border-amber-500/50 font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
          {t("deposit.verify")}
        </button>
        <p className="flex items-center justify-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" /> {t("deposit.autoCheck")}
        </p>
      </div>
    );
  }
  const r = RESULT[payment.status] || RESULT.FAILED;
  const Icon = r.icon;
  return (
    <div className="space-y-4 text-center" data-testid={`deposit-result-${payment.status.toLowerCase()}`}>
      <Icon className={`mx-auto h-16 w-16 ${r.cls}`} />
      <h3 className="font-display text-xl font-bold">{t(`deposit.${r.k}Title`)}</h3>
      <p className="text-sm text-slate-400">{t(`deposit.${r.k}Text`, { amount: formatXAF(payment.amount, lang) })}</p>
      <div className="grid grid-cols-2 gap-3 pt-2">
        <button onClick={onReset} className="h-12 rounded-full border border-white/15 font-semibold" data-testid="deposit-again-btn">{t("deposit.again")}</button>
        <button onClick={onClose} className="h-12 rounded-full bg-amber-500 font-semibold text-[#0B0D12]" data-testid="deposit-close-btn">{t("deposit.close")}</button>
      </div>
    </div>
  );
};

export const DepositDialog = ({ open, onOpenChange, initialPayment }) => {
  const { t } = useI18n();
  const { data: settings } = usePublicSettings();
  const [payment, setPayment] = useState(null);
  useEffect(() => {
    if (open) setPayment(initialPayment || null);
  }, [open, initialPayment]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl border-white/10 bg-[#121620] sm:max-w-md" data-testid="deposit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{t("deposit.title")}</DialogTitle>
        </DialogHeader>
        {!settings ? null : payment ? (
          <DepositStatus payment={payment} setPayment={setPayment} onReset={() => setPayment(null)} onClose={() => onOpenChange(false)} />
        ) : (
          <DepositForm settings={settings} onCreated={setPayment} />
        )}
      </DialogContent>
    </Dialog>
  );
};
